import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, CheckCircle, XCircle, RefreshCw, Download, Link2, FileText, DollarSign, LogOut, ClipboardCopy, Truck, Boxes, Trash2, Plus, Pencil, X, Save, Loader2, Power, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { PaymentLinkGenerator } from '@/components/PaymentLinkGenerator';
import { AdminRewards } from '@/components/AdminRewards';
import { EmailCampaigns } from '@/components/EmailCampaigns';
import { ProductImageManager } from '@/components/ProductImageManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export default function AdminOrders() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [tab, setTab] = useState('orders');
  const [rateInputs, setRateInputs] = useState({});
  const [trackingInputs, setTrackingInputs] = useState({});
  const [carrierInputs, setCarrierInputs] = useState({});
  const [shippingForOrder, setShippingForOrder] = useState(null);
  const [productEdit, setProductEdit] = useState(null); // null = closed, {} = new, {id...} = edit
  const [productSaving, setProductSaving] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [discoveryCalls, setDiscoveryCalls] = useState([]);
  const [dcForm, setDcForm] = useState({});
  const [couponCtrl, setCouponCtrl] = useState(null);
  const [couponSaving, setCouponSaving] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/orders`);
      setOrders(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('AdminOrders.fetchOrders failed', e);
      }
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReturns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/returns`);
      setReturns(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('AdminOrders.fetchReturns failed', e);
      }
      toast.error('Failed to load returns');
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/products`);
      setProducts(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('AdminOrders.fetchProducts failed', e);
      }
      toast.error('Failed to load catalogue');
    }
  }, []);

  const fetchWaitlist = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/waitlist`);
      setWaitlist(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('AdminOrders.fetchWaitlist failed', e);
      toast.error('Failed to load waitlist');
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchReturns();
    fetchProducts();
    fetchWaitlist();
    axios.get(`${API}/discovery-call/bookings`).then(r => setDiscoveryCalls(r.data)).catch(() => {});
    axios.get(`${API}/admin/coupon-controls/IIYPRA30`).then(r => setCouponCtrl(r.data)).catch(() => {});
  }, [fetchOrders, fetchReturns, fetchProducts, fetchWaitlist]);

  const refreshDiscoveryCalls = () => axios.get(`${API}/discovery-call/bookings`).then(r => setDiscoveryCalls(r.data)).catch(() => {});

  const saveCouponCtrl = async () => {
    if (!couponCtrl) return;
    setCouponSaving(true);
    try {
      const r = await axios.put(`${API}/admin/coupon-controls/IIYPRA30`, {
        enabled: !!couponCtrl.enabled,
        expiry_date: couponCtrl.expiry_date || '',
        max_orders_per_email: parseInt(couponCtrl.max_orders_per_email, 10) || 0,
      });
      setCouponCtrl(r.data);
      toast.success('Practitioner code settings saved');
    } catch (e) {
      toast.error('Failed to save code settings');
    } finally {
      setCouponSaving(false);
    }
  };

  const confirmBooking = async (id) => {
    const f = dcForm[id] || {};
    if (!f.confirmed_date || !f.confirmed_time) { toast.error('Set a date and time first'); return; }
    try {
      await axios.patch(`${API}/discovery-call/bookings/${id}/confirm`, {
        confirmed_date: f.confirmed_date, confirmed_time: f.confirmed_time,
        meeting_link: f.meeting_link || '', notes: f.notes || '',
      });
      toast.success('Confirmed — client emailed');
      refreshDiscoveryCalls();
    } catch { toast.error('Failed to confirm'); }
  };

  const toggleAvailability = async (product) => {
    const goingLive = !(product.available_for_purchase === true && product.status !== 'coming_soon');
    if (goingLive) {
      const n = waitlist.filter((w) => w.product_id === product.id && !w.notified_at).length;
      const msg = `Go LIVE with "${product.name}"?` + (n > 0 ? `\n\nThis will email ${n} waitlist subscriber${n === 1 ? '' : 's'} that it has launched.` : '');
      if (!window.confirm(msg)) return;
    } else {
      if (!window.confirm(`Set "${product.name}" back to Coming Soon (not buyable)?`)) return;
    }
    setTogglingId(product.id);
    try {
      const r = await axios.patch(`${API}/admin/products/${product.id}/availability`, { available: goingLive });
      const sent = r.data?.launch_emails_sent || 0;
      toast.success(goingLive ? `Now LIVE${sent ? ` · ${sent} launch email${sent === 1 ? '' : 's'} sent` : ''}` : 'Set to Coming Soon');
      fetchProducts();
      fetchWaitlist();
    } catch (e) {
      toast.error('Failed to update availability');
    } finally {
      setTogglingId(null);
    }
  };

  const exportWaitlistCsv = () => {
    if (!waitlist.length) { toast.error('Waitlist is empty'); return; }
    const rows = [['Email', 'Product', 'Signed up', 'Notified']].concat(
      waitlist.map((w) => [w.email, w.product_name || w.product_id || '', w.created_at || '', w.notified_at || ''])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleAccept = async (orderId) => {
    try { await axios.patch(`${API}/admin/orders/${orderId}/accept`); toast.success('Order accepted'); fetchOrders(); } catch (e) { toast.error('Failed'); }
  };

  const handleReject = async (orderId) => {
    try { await axios.patch(`${API}/admin/orders/${orderId}/reject`); toast.success('Order rejected'); fetchOrders(); } catch (e) { toast.error('Failed'); }
  };

  const handleSetRate = async (orderId) => {
    const rate = parseFloat(rateInputs[orderId]);
    if (!rate || rate < 25) { toast.error('Rate must be at least £25'); return; }
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/set-rate`, { rate_per_bottle: rate });
      toast.success('Custom rate set!');
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSendPaymentLink = async (orderId) => {
    try {
      const r = await axios.post(`${API}/admin/orders/${orderId}/payment-link`);
      toast.success('Payment link generated!');
      navigator.clipboard.writeText(r.data.payment_link);
      toast.info('Link copied to clipboard');
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate link'); }
  };

  const handleDownloadInvoice = (orderId) => {
    window.open(`${API}/admin/orders/${orderId}/invoice`, '_blank');
  };

  const handleExportXero = () => {
    window.open(`${API}/admin/export/xero`, '_blank');
  };

  const handleExportRoyalMail = () => {
    window.open(`${API}/admin/export/royal-mail?status=ready`, '_blank');
  };

  const [pingingIndexNow, setPingingIndexNow] = useState(false);
  const handleSyncSitemap = async () => {
    if (pingingIndexNow) return;
    setPingingIndexNow(true);
    try {
      const r = await axios.post(`${API}/admin/seo/sync-sitemap`);
      const { status, url_count, product_count, sitemap_written, indexnow } = r.data || {};
      const indexnowMsg = indexnow?.status === 'ok'
        ? `IndexNow ${indexnow.http} ✓`
        : `IndexNow ${indexnow?.status || 'error'}`;
      const fileMsg = sitemap_written ? 'sitemap.xml written' : 'file write skipped (read-only?)';
      if (status === 'ok') {
        toast.success(`Sitemap synced: ${url_count} URLs (${product_count} products) · ${fileMsg} · ${indexnowMsg}`);
      } else {
        toast.warning(`Partial sync: ${url_count} URLs · ${fileMsg} · ${indexnowMsg}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sitemap sync failed');
    } finally {
      setPingingIndexNow(false);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Delete "${productName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete product');
    }
  };

  const openProductCreate = () => setProductEdit({
    id: '', name: '', description: '', base_price: '', image: '', images: [], stock: '',
    status: 'active', available_for_purchase: true, available_from: '',
    features: [], benefits: [], ingredients: '',
  });
  const openProductEdit = (p) => setProductEdit({
    id: p.id || '', name: p.name || '', description: p.description || '',
    base_price: p.base_price ?? p.price ?? '',
    image: p.image || '', images: [...new Set((p.images || []).filter((u) => u && u !== p.image))], stock: p.stock ?? '',
    status: p.status || 'active',
    available_for_purchase: p.available_for_purchase !== false,
    available_from: p.available_from ? String(p.available_from).slice(0, 10) : '',
    features: Array.isArray(p.features) ? p.features : [],
    benefits: Array.isArray(p.benefits) ? p.benefits : [],
    ingredients: p.ingredients || '',
    _isEdit: true,
  });
  const closeProductEdit = () => setProductEdit(null);

  const handleProductFieldChange = (field, value) => setProductEdit((prev) => prev ? { ...prev, [field]: value } : prev);

  const handleProductSave = async () => {
    if (!productEdit) return;
    if (!productEdit.id?.trim()) { toast.error('Product ID is required.'); return; }
    if (!productEdit.name?.trim()) { toast.error('Product name is required.'); return; }
    const basePrice = parseFloat(productEdit.base_price);
    if (Number.isNaN(basePrice) || basePrice <= 0) { toast.error('Enter a valid base price (e.g. 44.99).'); return; }

    setProductSaving(true);
    try {
      const payload = {
        id: productEdit.id.trim(),
        name: productEdit.name.trim(),
        description: productEdit.description || '',
        base_price: basePrice,
        image: productEdit.image || '',
        images: productEdit.images || [],
        stock: productEdit.stock === '' ? null : parseInt(productEdit.stock, 10),
        status: productEdit.status,
        available_for_purchase: !!productEdit.available_for_purchase,
        available_from: productEdit.available_from ? `${productEdit.available_from}T00:00:00+01:00` : '',
        features: productEdit.features.filter(Boolean),
        benefits: productEdit.benefits.filter(Boolean),
        ingredients: productEdit.ingredients || '',
      };
      // Strip null/empty stock so it's not saved as null when blank on edit
      if (payload.stock === null) delete payload.stock;
      await axios.post(`${API}/admin/products`, payload);
      toast.success(productEdit._isEdit ? 'Product updated' : 'Product created');
      closeProductEdit();
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save product');
    } finally {
      setProductSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out');
    navigate('/admin/login', { replace: true });
  };

  const handleCopyAddress = async (order) => {
    const a = order.delivery_address || {};
    // Royal Mail Click & Drop friendly format - one field per line
    const lines = [
      `${a.first_name || ''} ${a.last_name || ''}`.trim(),
      a.company || '',
      a.address_line_1 || '',
      a.address_line_2 || '',
      a.city || '',
      a.county || '',
      a.postcode || '',
      a.country || 'United Kingdom',
      a.phone ? `Phone: ${a.phone}` : '',
      a.email ? `Email: ${a.email}` : '',
    ].filter(Boolean);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Shipping address copied - paste into Royal Mail Click & Drop');
    } catch (e) {
      toast.error('Clipboard blocked - select and copy manually');
    }
  };

  const handleMarkShipped = async (orderId) => {
    const tracking = (trackingInputs[orderId] || '').trim();
    const carrier = carrierInputs[orderId] || 'Royal Mail';
    if (!tracking) { toast.error('Tracking number is required'); return; }
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/ship`, { tracking_number: tracking, carrier });
      toast.success(`Marked as shipped \u2713 customer emailed with ${carrier} tracking ${tracking}`);
      setTrackingInputs({ ...trackingInputs, [orderId]: '' });
      setShippingForOrder(null);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark shipped');
    }
  };

  const handleApproveReturn = async (returnId) => {
    try { await axios.patch(`${API}/admin/returns/${returnId}/approve`); toast.success('Return approved'); fetchReturns(); } catch (e) { toast.error('Failed'); }
  };

  const handleDenyReturn = async (returnId) => {
    try { await axios.patch(`${API}/admin/returns/${returnId}/deny`); toast.success('Return denied'); fetchReturns(); } catch (e) { toast.error('Failed'); }
  };

  const filteredOrders = filter === 'all'
    ? orders
    : filter === 'practitioner'
      ? orders.filter(o => (o.coupon_code || '').toUpperCase() === 'IIYPRA30')
      : orders.filter(o => o.status === filter || o.type === filter);

  const statusBadge = (status) => {
    const styles = { pending_payment: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', accepted: 'bg-emerald-100 text-emerald-700', shipped: 'bg-cyan-100 text-cyan-800', rejected: 'bg-red-100 text-red-700', quote_pending: 'bg-amber-100 text-amber-700', quote_sent: 'bg-purple-100 text-purple-700' };
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>{status?.replace('_', ' ')}</span>;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary"></div></div>;

  return (
    <div className="min-h-screen py-24 md:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-heading" data-testid="admin-orders-title">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              {admin?.email ? <span className="text-emerald-700 font-medium">{admin.email}</span> : null}
              {admin?.email ? ' \u00b7 ' : ''}
              {orders.length} orders | {returns.length} returns
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleExportRoyalMail} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-border hover:border-red-500 text-red-700 transition-colors" data-testid="export-royal-mail" title="Export paid/accepted orders as Royal Mail Click & Drop CSV"><Truck className="w-4 h-4" /> Royal Mail CSV</button>
            <button onClick={handleExportXero} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-border hover:border-green-500 text-green-700 transition-colors" data-testid="export-xero"><Download className="w-4 h-4" /> Export Xero CSV</button>
            <button onClick={handleSyncSitemap} disabled={pingingIndexNow} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-border hover:border-blue-500 text-blue-700 transition-colors disabled:opacity-50" data-testid="sync-sitemap" title="Regenerate sitemap.xml from DB + ping Bing, Yandex & IndexNow consortium for instant re-indexing">{pingingIndexNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {pingingIndexNow ? 'Syncing…' : 'Sync Sitemap'}</button>
            <button onClick={fetchOrders} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-border hover:border-primary transition-colors" data-testid="refresh-orders"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={handleLogout} className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-border hover:border-red-400 text-red-600 transition-colors" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('orders')} data-testid="tab-orders" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'orders' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Orders</button>
          <button onClick={() => setTab('returns')} data-testid="tab-returns" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'returns' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Returns ({returns.length})</button>
          <button onClick={() => setTab('catalogue')} data-testid="tab-catalogue" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'catalogue' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Catalogue ({products.length})</button>
          <button onClick={() => setTab('waitlist')} data-testid="tab-waitlist" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'waitlist' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Waitlist ({waitlist.length})</button>
          <button onClick={() => setTab('discovery')} data-testid="tab-discovery" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'discovery' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Discovery Calls ({discoveryCalls.length})</button>
          <button onClick={() => setTab('linkgen')} data-testid="tab-linkgen" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'linkgen' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Payment Links</button>
          <button onClick={() => setTab('rewards')} data-testid="tab-rewards" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'rewards' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Rewards</button>
          <button onClick={() => setTab('campaigns')} data-testid="tab-campaigns" className={`px-5 py-2.5 rounded-xl font-medium transition-all ${tab === 'campaigns' ? 'bg-primary text-white' : 'bg-white border border-border'}`}>Email Campaigns</button>
        </div>

        {tab === 'linkgen' && (
          <PaymentLinkGenerator />
        )}

        {tab === 'rewards' && (
          <AdminRewards />
        )}

        {tab === 'campaigns' && (
          <EmailCampaigns />
        )}

        {tab === 'orders' && (
          <>
            {couponCtrl && (
              <div className="bg-white rounded-2xl p-5 shadow-sm mb-6 border border-indigo-100" data-testid="coupon-controls-panel">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <h3 className="font-semibold text-base">Practitioner code — <span className="font-mono text-indigo-700">IIYPRA30</span></h3>
                    <p className="text-xs text-muted-foreground">£30/bottle, any quantity. Hidden from the public — set an expiry or a per-customer order cap so it isn't over-shared.</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input type="checkbox" checked={!!couponCtrl.enabled} onChange={(e) => setCouponCtrl({ ...couponCtrl, enabled: e.target.checked })} data-testid="coupon-ctrl-enabled" />
                    {couponCtrl.enabled ? 'Active' : 'Disabled'}
                  </label>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Expiry date (optional)</label>
                    <input type="date" value={couponCtrl.expiry_date || ''} onChange={(e) => setCouponCtrl({ ...couponCtrl, expiry_date: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600/30 focus:border-indigo-600 text-sm" data-testid="coupon-ctrl-expiry" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Max paid orders per customer (0 = unlimited)</label>
                    <input type="number" min="0" value={couponCtrl.max_orders_per_email ?? 0} onChange={(e) => setCouponCtrl({ ...couponCtrl, max_orders_per_email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600/30 focus:border-indigo-600 text-sm" data-testid="coupon-ctrl-cap" />
                  </div>
                  <button onClick={saveCouponCtrl} disabled={couponSaving} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors" data-testid="coupon-ctrl-save">
                    {couponSaving ? 'Saving…' : 'Save settings'}
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-2 mb-6 flex-wrap">
              {['all', 'practitioner', 'pending_payment', 'paid', 'accepted', 'shipped', 'quote_pending', 'quote_sent', 'wholesale', 'rejected'].map((f) => (
                <button key={f} data-testid={`order-filter-${f}`} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filter === f ? 'bg-secondary text-white' : 'bg-white border border-border hover:border-primary'}`}>{f === 'practitioner' ? 'Practitioner (IIYPRA30)' : f.replace('_', ' ')}</button>
              ))}
            </div>

            {filteredOrders.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl"><Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No orders found</p></div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order, index) => (
                  <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="bg-white rounded-2xl p-6 shadow-sm" data-testid={`order-row-${index}`}>
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        {statusBadge(order.status)}
                        {order.type === 'wholesale' && <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">WHOLESALE</span>}
                        {order.coupon_code && <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold" data-testid={`order-coupon-${index}`}>CODE: {order.coupon_code}</span>}
                        <span className="text-xs text-muted-foreground font-mono">{order.invoice_number || order.id?.slice(0, 8)}</span>
                        <span className="text-xs text-muted-foreground">{order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="font-semibold">{order.delivery_address?.first_name} {order.delivery_address?.last_name}</p>
                          <p className="text-muted-foreground">{order.delivery_address?.email}</p>
                          <p className="text-muted-foreground">{order.delivery_address?.phone}</p>
                          <p className="text-muted-foreground">{order.delivery_address?.address_line_1}, {order.delivery_address?.postcode}</p>
                        </div>
                        <div>
                          <p className="font-medium">{order.product_name}</p>
                          <p className="text-muted-foreground">{order.quantity} bottle{order.quantity > 1 ? 's' : ''}</p>
                          <p className="text-muted-foreground">{order.delivery_option_name}</p>
                        </div>
                        <div>
                          {order.price_per_bottle ? (
                            <>
                              <p className="text-muted-foreground">Rate: £{order.price_per_bottle?.toFixed(2)}/bottle</p>
                              <p className="text-muted-foreground">Subtotal: £{order.subtotal?.toFixed(2)}</p>
                              <p className="text-muted-foreground">VAT: £{order.vat_amount?.toFixed(2)}</p>
                              <p className="font-bold text-lg">Total: £{order.total?.toFixed(2)}</p>
                            </>
                          ) : (
                            <p className="text-amber-700 font-medium">Awaiting custom quote</p>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
                        <button onClick={() => handleCopyAddress(order)} className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-medium" data-testid={`copy-address-btn-${index}`} title="Copy shipping address - paste into Royal Mail Click & Drop"><ClipboardCopy className="w-3 h-3" /> Copy Address</button>
                        {order.type === 'wholesale' && order.status === 'quote_pending' && (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center border rounded-lg overflow-hidden">
                              <span className="px-3 py-2 bg-gray-50 text-sm">£</span>
                              <input type="number" min="25" step="0.50" placeholder="Rate/bottle" value={rateInputs[order.id] || ''} onChange={(e) => setRateInputs({...rateInputs, [order.id]: e.target.value})} className="px-3 py-2 w-24 focus:outline-none text-sm" />
                            </div>
                            <button onClick={() => handleSetRate(order.id)} className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><DollarSign className="w-3 h-3" /> Set Rate</button>
                          </div>
                        )}
                        {order.type === 'wholesale' && order.status === 'quote_sent' && (
                          <button onClick={() => handleSendPaymentLink(order.id)} className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><Link2 className="w-3 h-3" /> Generate Payment Link</button>
                        )}
                        {(order.status === 'paid' || order.status === 'accepted') && (
                          <button onClick={() => handleDownloadInvoice(order.id)} className="flex items-center gap-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><FileText className="w-3 h-3" /> Download Invoice</button>
                        )}
                        {(order.status === 'paid' || order.status === 'accepted') && (
                          <>
                            {shippingForOrder === order.id ? (
                              <div className="flex items-center gap-2">
                                <select
                                  value={carrierInputs[order.id] || 'Royal Mail'}
                                  onChange={(e) => setCarrierInputs({...carrierInputs, [order.id]: e.target.value})}
                                  className="px-2 py-2 border rounded-lg text-xs focus:outline-none focus:border-cyan-600"
                                  data-testid={`carrier-select-${index}`}
                                >
                                  <option value="Royal Mail">Royal Mail</option>
                                  <option value="Evri">Evri</option>
                                  <option value="InPost">InPost (Yodel)</option>
                                  <option value="Yodel">Yodel</option>
                                </select>
                                <input
                                  type="text"
                                  value={trackingInputs[order.id] || ''}
                                  onChange={(e) => setTrackingInputs({...trackingInputs, [order.id]: e.target.value})}
                                  placeholder={`${carrierInputs[order.id] || 'Royal Mail'} tracking #`}
                                  className="px-3 py-2 border rounded-lg text-xs focus:outline-none focus:border-cyan-600 w-44"
                                  data-testid={`tracking-input-${index}`}
                                />
                                <button onClick={() => handleMarkShipped(order.id)} className="bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 rounded-lg text-xs font-medium" data-testid={`save-shipped-${index}`}>Save & Email</button>
                                <button onClick={() => setShippingForOrder(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs">Cancel</button>
                              </div>
                            ) : (
                              <button onClick={() => setShippingForOrder(order.id)} className="flex items-center gap-1 bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 rounded-lg text-xs font-medium" data-testid={`mark-shipped-btn-${index}`}><Truck className="w-3 h-3" /> Mark as Shipped</button>
                            )}
                          </>
                        )}
                        {order.status === 'shipped' && (
                          <span className="flex items-center gap-1 bg-cyan-50 text-cyan-700 px-3 py-2 rounded-lg text-xs font-medium border border-cyan-200">
                            <Truck className="w-3 h-3" /> Shipped &middot; {order.carrier || 'Royal Mail'} &middot; <code className="font-mono">{order.tracking_number}</code>
                          </span>
                        )}
                        {(order.status === 'paid' || order.status === 'pending_payment') && (
                          <>
                            <button onClick={() => handleAccept(order.id)} className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><CheckCircle className="w-3 h-3" /> Accept</button>
                            <button onClick={() => handleReject(order.id)} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><XCircle className="w-3 h-3" /> Reject</button>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'returns' && (
          <div className="space-y-4">
            {returns.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl"><Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No return requests</p></div>
            ) : returns.map((ret, i) => (
              <motion.div key={ret.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${ret.status === 'approved' ? 'bg-green-100 text-green-700' : ret.status === 'denied' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{ret.status}</span>
                  <span className="text-xs text-muted-foreground">{new Date(ret.created_at).toLocaleDateString('en-GB')}</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div><p className="font-semibold">{ret.customer_name}</p><p className="text-muted-foreground">{ret.email}</p><p className="text-muted-foreground">Order: {ret.order_number}</p></div>
                  <div><p className="font-medium">Reason:</p><p className="text-muted-foreground">{ret.reason}</p>{ret.additional_comments && <p className="text-xs text-muted-foreground mt-1">{ret.additional_comments}</p>}</div>
                  <div><p className="text-muted-foreground">Preference: <span className="font-medium">{ret.resolution_preference}</span></p></div>
                </div>
                {ret.status === 'pending_review' && (
                  <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                    <button onClick={() => handleApproveReturn(ret.id)} className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><CheckCircle className="w-3 h-3" /> Approve</button>
                    <button onClick={() => handleDenyReturn(ret.id)} className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium"><XCircle className="w-3 h-3" /> Deny</button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
        {tab === 'catalogue' && (
          <div className="space-y-4" data-testid="catalogue-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 flex-1 min-w-[260px]">
                <p><strong>Live catalogue.</strong> Products are seeded from <code className="bg-amber-100 px-1 rounded">backend/seed_data/products.json</code> on every fresh deploy. Edits made here persist in the database; if a product also exists in the seed file, its values will be re-applied on the next blank-DB deploy.</p>
              </div>
              <button onClick={openProductCreate} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors" data-testid="catalogue-add-product">
                <Plus className="w-4 h-4" /> Add product
              </button>
            </div>
            {products.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl"><Boxes className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No products in the catalogue</p></div>
            ) : products.map((product, i) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="bg-white rounded-2xl p-6 shadow-sm flex flex-col md:flex-row gap-5" data-testid={`product-row-${i}`}>
                {product.image && (
                  <img loading="lazy" decoding="async" src={product.image} alt={product.name} className="w-24 h-24 object-cover rounded-xl bg-gray-100 self-start" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3 flex-wrap mb-2">
                    <h3 className="font-semibold text-lg">{product.name}</h3>
                    {product.status === 'coming_soon' && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Coming soon</span>}
                    {product.available_from && product.status === 'coming_soon' && (() => { const d = new Date(product.available_from); return !isNaN(d) ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium" data-testid={`product-schedule-${i}`}>Goes live {d.toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' })}</span> : null; })()}
                    {product.available_for_purchase === false && product.status !== 'coming_soon' && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">Hidden</span>}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{product.description}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <span><span className="text-muted-foreground">Base:</span> <span className="font-medium">£{(product.base_price ?? product.price ?? 0).toFixed(2)}</span></span>
                    <span><span className="text-muted-foreground">Stock:</span> <span className="font-medium">{product.stock ?? '—'}</span></span>
                    <span><span className="text-muted-foreground">Tiers:</span> <span className="font-medium">{(product.pricing_tiers || []).length}</span></span>
                    <span><span className="text-muted-foreground">Orders:</span> <span className="font-medium">{product.order_count ?? 0}</span></span>
                    <span className="text-xs text-muted-foreground font-mono">{product.id?.slice(0, 8)}…</span>
                  </div>
                </div>
                <div className="flex md:flex-col gap-2 md:items-end">
                  <button
                    onClick={() => toggleAvailability(product)}
                    disabled={togglingId === product.id}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-white disabled:opacity-60 ${product.available_for_purchase === true && product.status !== 'coming_soon' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                    data-testid={`toggle-availability-btn-${i}`}
                    title={product.available_for_purchase === true && product.status !== 'coming_soon' ? 'Set back to Coming Soon' : 'Make this product buyable now'}
                  >
                    {togglingId === product.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Power className="w-3 h-3" />}
                    {product.available_for_purchase === true && product.status !== 'coming_soon' ? 'Set Coming Soon' : 'Go Live'}
                  </button>
                  <button
                    onClick={() => openProductEdit(product)}
                    className="flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                    data-testid={`edit-product-btn-${i}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteProduct(product.id, product.name)}
                    disabled={(product.order_count ?? 0) > 0}
                    title={(product.order_count ?? 0) > 0 ? 'Product is referenced by existing orders' : 'Delete product'}
                    className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg text-xs font-medium"
                    data-testid={`delete-product-btn-${i}`}
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {tab === 'waitlist' && (
          <div className="space-y-4" data-testid="waitlist-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">Launch waitlist sign-ups (Notify-Me). Turning a product "Go Live" auto-emails everyone here who hasn't been notified yet.</p>
              <button onClick={exportWaitlistCsv} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-colors" data-testid="waitlist-export-csv">
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>
            {waitlist.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl"><Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No waitlist sign-ups yet</p></div>
            ) : (
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Product</th>
                      <th className="px-5 py-3">Signed up</th>
                      <th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {waitlist.map((w, i) => (
                      <tr key={w.id || i} data-testid={`waitlist-row-${i}`}>
                        <td className="px-5 py-3 font-medium">{w.email}</td>
                        <td className="px-5 py-3">{w.product_name || w.product_id || '—'}</td>
                        <td className="px-5 py-3 text-muted-foreground">{w.created_at ? new Date(w.created_at).toLocaleDateString('en-GB') : '—'}</td>
                        <td className="px-5 py-3">
                          {w.notified_at
                            ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">Notified</span>
                            : <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Waiting</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'discovery' && (
          <div className="space-y-4" data-testid="discovery-panel">
            <p className="text-sm text-muted-foreground">Free Discovery Call requests. Set a confirmed date &amp; time (and optional meeting link) to auto-email the client a confirmation.</p>
            {discoveryCalls.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl"><p className="text-xl text-muted-foreground">No Discovery Call requests yet</p></div>
            ) : (
              <div className="space-y-4">
                {discoveryCalls.map((b, i) => (
                  <div key={b.id} className="bg-white rounded-2xl p-5 shadow-sm border border-border" data-testid={`discovery-row-${i}`}>
                    <div className="mb-3">
                      <p className="font-bold text-emerald-950">{b.full_name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${b.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span></p>
                      <p className="text-sm text-muted-foreground">{b.email} · {b.phone} · {b.method === 'phone' ? 'Phone' : 'Video'}</p>
                      <p className="text-xs text-muted-foreground mt-1">Preferred: {b.preferred_date || '—'} {b.preferred_time}. {b.message ? `“${b.message}”` : ''}</p>
                      {b.status === 'confirmed' && <p className="text-xs text-emerald-700 mt-1">Confirmed for {b.confirmed_date} at {b.confirmed_time}{b.meeting_link ? ` · ${b.meeting_link}` : ''}</p>}
                    </div>
                    {b.status !== 'confirmed' && (
                      <div className="grid sm:grid-cols-4 gap-2 items-end border-t border-border pt-3">
                        <input type="date" onChange={(e) => setDcForm(s => ({ ...s, [b.id]: { ...s[b.id], confirmed_date: e.target.value } }))} className="border-2 border-border rounded-lg px-3 py-2 text-sm" data-testid={`dc-date-${i}`} />
                        <input type="time" onChange={(e) => setDcForm(s => ({ ...s, [b.id]: { ...s[b.id], confirmed_time: e.target.value } }))} className="border-2 border-border rounded-lg px-3 py-2 text-sm" data-testid={`dc-time-${i}`} />
                        <input placeholder="Zoom/Teams link (optional)" onChange={(e) => setDcForm(s => ({ ...s, [b.id]: { ...s[b.id], meeting_link: e.target.value } }))} className="border-2 border-border rounded-lg px-3 py-2 text-sm" data-testid={`dc-link-${i}`} />
                        <button onClick={() => confirmBooking(b.id)} className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-2 rounded-lg text-sm" data-testid={`dc-confirm-${i}`}>Confirm &amp; Email</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        <AnimatePresence>
          {productEdit && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-8 overflow-y-auto"
              onClick={closeProductEdit}
              data-testid="product-edit-modal"
            >
              <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }}
                transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl my-auto"
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                  <h3 className="text-lg font-semibold">{productEdit._isEdit ? `Edit \u201c${productEdit.name || productEdit.id}\u201d` : 'Add new product'}</h3>
                  <button onClick={closeProductEdit} className="p-1 text-slate-500 hover:text-slate-700" aria-label="Close" data-testid="product-edit-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Product ID *</label>
                      <input
                        type="text" value={productEdit.id}
                        onChange={(e) => handleProductFieldChange('id', e.target.value)}
                        disabled={productEdit._isEdit}
                        placeholder="biocurcumax-bcm95"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 disabled:bg-slate-100 font-mono text-sm"
                        data-testid="product-edit-id"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
                      <select
                        value={productEdit.status}
                        onChange={(e) => handleProductFieldChange('status', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                        data-testid="product-edit-status"
                      >
                        <option value="active">Active</option>
                        <option value="coming_soon">Coming soon</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text" value={productEdit.name}
                      onChange={(e) => handleProductFieldChange('name', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                      data-testid="product-edit-name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Description</label>
                    <textarea
                      rows={3} value={productEdit.description}
                      onChange={(e) => handleProductFieldChange('description', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                      data-testid="product-edit-description"
                    />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Base price (\u00a3) *</label>
                      <input
                        type="number" step="0.01" min="0" value={productEdit.base_price}
                        onChange={(e) => handleProductFieldChange('base_price', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                        data-testid="product-edit-price"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Stock</label>
                      <input
                        type="number" min="0" value={productEdit.stock}
                        onChange={(e) => handleProductFieldChange('stock', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                        data-testid="product-edit-stock"
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                          type="checkbox" checked={!!productEdit.available_for_purchase}
                          onChange={(e) => handleProductFieldChange('available_for_purchase', e.target.checked)}
                          data-testid="product-edit-available"
                        />
                        Available to buy
                      </label>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Scheduled go-live date (optional)</label>
                      <input
                        type="date" value={productEdit.available_from || ''}
                        onChange={(e) => handleProductFieldChange('available_from', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                        data-testid="product-edit-available-from"
                      />
                      <p className="text-[11px] text-slate-500 mt-1">If set and the product is "Coming soon", it goes live automatically at the start of this day (UK time) and emails the waitlist.</p>
                    </div>
                  </div>

                  <ProductImageManager
                    images={[productEdit.image, ...(productEdit.images || [])].filter(Boolean)}
                    onChange={(list) => setProductEdit((prev) => prev ? { ...prev, image: list[0] || '', images: list.slice(1) } : prev)}
                  />

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Ingredients</label>
                    <textarea
                      rows={2} value={productEdit.ingredients}
                      onChange={(e) => handleProductFieldChange('ingredients', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                      data-testid="product-edit-ingredients"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Features (one per line)</label>
                    <textarea
                      rows={3}
                      value={(productEdit.features || []).join('\n')}
                      onChange={(e) => handleProductFieldChange('features', e.target.value.split('\n'))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                      data-testid="product-edit-features"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Benefits (one per line, format: <em>Heading - description</em>)</label>
                    <textarea
                      rows={3}
                      value={(productEdit.benefits || []).join('\n')}
                      onChange={(e) => handleProductFieldChange('benefits', e.target.value.split('\n'))}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 text-sm"
                      data-testid="product-edit-benefits"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
                  <button onClick={closeProductEdit} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-sm font-medium" data-testid="product-edit-cancel">Cancel</button>
                  <button onClick={handleProductSave} disabled={productSaving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white text-sm font-medium" data-testid="product-edit-save">
                    {productSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {productSaving ? 'Saving\u2026' : 'Save product'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
